/**
 * useUpdateProfile Hook
 * Mutation for updating user profiles with cache invalidation
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { profileRepo } from '@/lib/repositories'
import type { UpdateProfileData } from '@/lib/repositories/interfaces/IProfileRepository'
import { PROFILE_QUERY_KEY } from './useProfile'
import { INSIGHTS_QUERY_KEY } from './useInsights'
import { CURRENT_USER_QUERY_KEY } from './useCurrentUser'
import { analytics } from '@/lib/services/analytics'
import { AnalyticsEvents } from '@/lib/services/analytics-events'

interface UpdateProfileParams {
  username: string
  data: UpdateProfileData
}

export function useUpdateProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ username, data }: UpdateProfileParams) =>
      profileRepo.updateProfile(username, data),
    onSuccess: (_updatedProfile, { username, data }) => {
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY(username) })
      analytics.capture(AnalyticsEvents.PROFILE_UPDATED)

      // If username changed, also invalidate the new profile key
      if (data.username && data.username !== username) {
        queryClient.invalidateQueries({
          queryKey: PROFILE_QUERY_KEY(data.username),
        })
        // Invalidate current user since their username changed
        queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY })
      }

      // Also invalidate insights since they include display names/usernames
      queryClient.invalidateQueries({ queryKey: INSIGHTS_QUERY_KEY })
    },
  })
}
